import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Stack,
  Tooltip,
  Paper,
  Divider,
} from '@mui/material';
import {
  PersonAdd,
  Delete,
  Edit,
  ContentCopy,
  CheckCircle,
  Email,
  Close,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import {api} from '../config/API_URL';

export default function TeamManagement() {
  const [teamMembers, setTeamMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openInviteDialog, setOpenInviteDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', severity: 'success' });
  
  const [inviteForm, setInviteForm] = useState({
    email: '',
    role: 'member',
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    loadTeamData();
  }, []);

  const loadTeamData = async () => {
    try {
      const [membersRes, invitesRes] = await Promise.all([
        api.get('/team/members'),
        api.get('/team/invites'),
      ]);

      setTeamMembers(membersRes.data.members || []);
      setInvites(invitesRes.data.invites || []);
    } catch (err) {
      console.error('Failed to load team data:', err);
      showAlert('Failed to load team data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showAlert = (message, severity = 'success') => {
    setAlert({ show: true, message, severity });
    setTimeout(() => {
      setAlert({ show: false, message: '', severity: 'success' });
    }, 5000);
  };

  const handleInviteChange = (field) => (event) => {
    setInviteForm({
      ...inviteForm,
      [field]: event.target.value,
    });
    // Clear error for this field
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: '',
      });
    }
  };

  const validateInvite = () => {
    const newErrors = {};

    if (!inviteForm.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inviteForm.email)) {
      newErrors.email = 'Invalid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSendInvite = async () => {
    if (!validateInvite()) {
      return;
    }

    setSubmitting(true);

    try {
      const response = await api.post('/team/invite', {
        email: inviteForm.email,
        role: inviteForm.role,
      });

      if (response.data.success) {
        showAlert('Invite sent successfully!');
        setOpenInviteDialog(false);
        setInviteForm({ email: '', role: 'member' });
        loadTeamData(); // Reload to show new pending invite
      }
    } catch (err) {
      console.error('Failed to send invite:', err);
      showAlert(
        err.response?.data?.message || 'Failed to send invite',
        'error'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopyInviteLink = (inviteCode) => {
    const inviteLink = `${window.location.origin}/join?code=${inviteCode}`;
    navigator.clipboard.writeText(inviteLink);
    showAlert('Invite link copied to clipboard!');
  };

  const handleDeleteInvite = async (inviteId) => {
    if (!window.confirm('Are you sure you want to delete this invite?')) {
      return;
    }

    try {
      await api.delete(`/team/invite/${inviteId}`);
      showAlert('Invite deleted successfully');
      loadTeamData();
    } catch (err) {
      console.error('Failed to delete invite:', err);
      showAlert('Failed to delete invite', 'error');
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!window.confirm('Are you sure you want to remove this team member?')) {
      return;
    }

    try {
      await api.delete(`/team/member/${userId}`);
      showAlert('Team member removed successfully');
      loadTeamData();
    } catch (err) {
      console.error('Failed to remove member:', err);
      showAlert('Failed to remove team member', 'error');
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      await api.patch(`/team/member/${userId}`, {
        role: newRole,
      });
      showAlert('Role updated successfully');
      loadTeamData();
    } catch (err) {
      console.error('Failed to update role:', err);
      showAlert('Failed to update role', 'error');
    }
  };

  const getRoleBadge = (role) => {
    const roleConfig = {
      owner: { color: 'warning', label: 'Owner' },
      manager: { color: 'primary', label: 'Manager' },
      member: { color: 'default', label: 'Staff Member' },
    };

    const config = roleConfig[role] || roleConfig.member;

    return (
      <Chip
        label={config.label}
        color={config.color}
        size="small"
        sx={{ fontWeight: 600 }}
      />
    );
  };

  const getInviteStatusBadge = (invite) => {
    if (invite.used) {
      return <Chip label="Accepted" color="success" size="small" />;
    }

    const expiresAt = new Date(invite.expires_at);
    const now = new Date();

    if (expiresAt < now) {
      return <Chip label="Expired" color="error" size="small" />;
    }

    return <Chip label="Pending" color="warning" size="small" />;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Alert */}
      <AnimatePresence>
        {alert.show && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <Alert
              severity={alert.severity}
              sx={{ mb: 3 }}
              onClose={() => setAlert({ ...alert, show: false })}
            >
              {alert.message}
            </Alert>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Team Members Card */}
      <Card sx={{ mb: 4 }}>
        <CardContent sx={{ p: 4 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                Team Members
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage your team and their roles
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<PersonAdd />}
              onClick={() => setOpenInviteDialog(true)}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                fontWeight: 600,
              }}
            >
              Invite Employee
            </Button>
          </Stack>

          <Divider sx={{ mb: 3 }} />

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'rgba(102, 126, 234, 0.05)' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Role</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Joined</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {teamMembers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No team members yet. Invite your first employee!
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  teamMembers.map((member) => (
                    <TableRow
                      key={member.id}
                      sx={{
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.02)',
                        },
                      }}
                    >
                      <TableCell>
                        <Typography sx={{ fontWeight: 600 }}>
                          {member.username}
                        </Typography>
                      </TableCell>
                      <TableCell>{member.email}</TableCell>
                      <TableCell>{getRoleBadge(member.role)}</TableCell>
                      <TableCell>
                        {new Date(member.joined_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell align="right">
                        {member.role !== 'owner' && (
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Tooltip title="Change Role">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const newRole = member.role === 'manager' ? 'member' : 'manager';
                                  handleUpdateRole(member.id, newRole);
                                }}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Remove">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRemoveMember(member.id)}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        )}
                        {member.role === 'owner' && (
                          <Typography variant="caption" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Pending Invites Card */}
      <Card>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
            Pending Invites
          </Typography>

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'rgba(102, 126, 234, 0.05)' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Role</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Sent</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invites.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No pending invites
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  invites.map((invite) => (
                    <TableRow
                      key={invite.id}
                      sx={{
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.02)',
                        },
                      }}
                    >
                      <TableCell>{invite.email}</TableCell>
                      <TableCell>{getRoleBadge(invite.role)}</TableCell>
                      <TableCell>{getInviteStatusBadge(invite)}</TableCell>
                      <TableCell>
                        {new Date(invite.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell align="right">
                        {!invite.used && (
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Tooltip title="Copy Invite Link">
                              <IconButton
                                size="small"
                                onClick={() => handleCopyInviteLink(invite.invite_code)}
                              >
                                <ContentCopy fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Invite">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteInvite(invite.id)}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Invite Dialog */}
      <Dialog
        open={openInviteDialog}
        onClose={() => !submitting && setOpenInviteDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Invite New Team Member
            </Typography>
            <IconButton
              onClick={() => setOpenInviteDialog(false)}
              disabled={submitting}
            >
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <TextField
              label="Email Address"
              type="email"
              value={inviteForm.email}
              onChange={handleInviteChange('email')}
              error={!!errors.email}
              helperText={errors.email || 'Enter the email of the person you want to invite'}
              fullWidth
              required
              disabled={submitting}
              InputProps={{
                startAdornment: (
                  <Email sx={{ mr: 1, color: 'action.active' }} />
                ),
              }}
            />

            <TextField
              select
              label="Role"
              value={inviteForm.role}
              onChange={handleInviteChange('role')}
              fullWidth
              required
              disabled={submitting}
              helperText="Select the role for this team member"
            >
              <MenuItem value="manager">Manager</MenuItem>
              <MenuItem value="member">Staff Member</MenuItem>
            </TextField>

            <Alert severity="info">
              An invite link will be sent to this email address. The link will expire in 7 days.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setOpenInviteDialog(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSendInvite}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={20} /> : <Email />}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            }}
          >
            {submitting ? 'Sending...' : 'Send Invite'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
